// Piezas de interfaz compartidas por las tres vistas de Reportes Recepción (simple, Pro e
// Históricas): encabezado con migas y acciones, botones, selector de filtro y tabla simple.
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Card, Typography } from '@mui/material';
import { COLOR, FUENTE } from './tokens';

// Respaldo del filtro de tipo de trámite, por si la API del catálogo no responde. La lista real
// se pide a /datos-maestros/tipos-turno.
export const TIPOS_TRAMITE_FALLBACK = ['RTI', 'ART', 'JUBILACION', 'PENSION', 'REAJUSTE', 'GANANCIAS', 'MINUSVALIA', 'DECLARATORIA_HEREDEROS', 'AMPARO', 'GENERAL'];

// ANSES queda excluido de todas las estadísticas de Recepción por decisión de negocio, así que
// ofrecerlo como filtro devolvería siempre una lista vacía.
export const TIPO_EXCLUIDO_DE_ESTADISTICAS = 'ANSES';

export function BotonSecundario({ children, onClick, primario = false, disabled = false }) {
    return (
        <button
            type="button" onClick={onClick} disabled={disabled}
            style={{
                fontFamily: FUENTE, fontSize: 12.5, fontWeight: 600, borderRadius: 8, padding: '8px 14px',
                cursor: disabled ? 'default' : 'pointer', opacity: disabled ? 0.55 : 1,
                border: primario ? 'none' : '1px solid #e2e8f0',
                background: primario ? COLOR.azul : '#fff',
                color: primario ? '#fff' : COLOR.negro,
            }}
        >
            {children}
        </button>
    );
}

/** Migas + título + subtítulo a la izquierda, acciones a la derecha. */
export function EncabezadoReportes({ migas = [], titulo, subtitulo, acciones }) {
    const navigate = useNavigate();
    return (
        <>
            <nav aria-label="breadcrumb" className="mb-3">
                <ol className="breadcrumb" style={{ marginBottom: 0 }}>
                    {migas.map((miga, i) => (
                        <li key={miga.label} className={`breadcrumb-item${i === migas.length - 1 ? ' active' : ''}`}>
                            {miga.to && i < migas.length - 1 ? (
                                <span role="button" style={{ cursor: 'pointer', color: COLOR.azul, textDecoration: 'underline' }} onClick={() => navigate(miga.to)}>
                                    {miga.label}
                                </span>
                            ) : miga.label}
                        </li>
                    ))}
                </ol>
            </nav>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', flexWrap: 'wrap', gap: '12px', marginBottom: '16px' }}>
                <Box>
                    <Typography sx={{ fontFamily: FUENTE, fontSize: 31, fontWeight: 700, letterSpacing: '-0.025em', color: COLOR.negro }}>
                        {titulo}
                    </Typography>
                    {subtitulo && (
                        <Typography sx={{ fontFamily: FUENTE, fontSize: 13, color: COLOR.grisTexto, marginTop: '2px' }}>
                            {subtitulo}
                        </Typography>
                    )}
                </Box>
                <Box sx={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                    {acciones}
                </Box>
            </Box>
        </>
    );
}

export function SelectFiltro({ label, valor, onChange, opciones, textoTodos = 'Todos' }) {
    return (
        <Box>
            <Typography sx={{ fontFamily: FUENTE, fontSize: 11, fontWeight: 700, textTransform: 'uppercase', color: COLOR.grisTexto, marginBottom: '4px' }}>
                {label}
            </Typography>
            <select
                value={valor}
                onChange={(e) => onChange(e.target.value)}
                style={{ fontFamily: FUENTE, fontSize: 13,  borderRadius: 8, padding: '7px 10px', height: 34, minWidth: 160, background: '#fff' }}
            >
                <option value="">{textoTodos}</option>
                {opciones.map((o) => (
                    <option key={o.valor} value={o.valor}>{o.etiqueta}</option>
                ))}
            </select>
        </Box>
    );
}

/** Tarjeta con título, subtítulo opcional y contenido. */
export function TarjetaReporte({ titulo, subtitulo, children }) {
    return (
        <Card variant="outlined" sx={{ border: '1px solid #e2e8f0', borderRadius: '12px', boxShadow: 'none', padding: '20px 22px' }}>
            <Typography sx={{ fontFamily: FUENTE, fontSize: 16, fontWeight: 700, color: COLOR.negro }}>{titulo}</Typography>
            {subtitulo && (
                <Typography sx={{ fontFamily: FUENTE, fontSize: 12.5, color: COLOR.grisTexto, marginBottom: '14px' }}>{subtitulo}</Typography>
            )}
            <Box sx={{ marginTop: subtitulo ? 0 : '14px' }}>{children}</Box>
        </Card>
    );
}

const formatearValor = (valor, esPorcentaje) => {
    if (valor === null || valor === undefined || valor === '') return '—';
    if (typeof valor !== 'number') return valor;
    const texto = Number.isInteger(valor) ? valor.toLocaleString('es-AR') : valor.toLocaleString('es-AR', { maximumFractionDigits: 1 });
    return esPorcentaje ? `${texto}%` : texto;
};

/**
 * Tabla liviana para la vista simple.
 * @param {{columnas: Array<{titulo: string, alinear?: 'left'|'right', porcentaje?: boolean, grupo?: string}>,
 *   filas: Array<{clave: string, celdas: Array, atenuada?: boolean, destacada?: boolean}>, vacio?: string}} props
 */
// En el celular los encabezados pueden partirse en dos renglones y el relleno es menor: así una
// tabla de 4 columnas entra completa en 360-390 px. Las más anchas (por día) siguen con su propio
// scroll horizontal dentro de la tarjeta.
export function TablaReporte({ columnas, filas, vacio = 'Sin datos en el período seleccionado.' }) {
    if (!filas.length) {
        return <Typography sx={{ fontFamily: FUENTE, fontSize: 13, color: COLOR.grisTexto }}>{vacio}</Typography>;
    }
    return (
        <Box sx={{ overflowX: 'auto' }}>
            <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse', fontFamily: FUENTE }}>
                <Box component="thead">
                    <Box component="tr">
                        {columnas.map((c, i) => (
                            <Box
                                component="th" key={`${c.titulo}-${i}`}
                                sx={{
                                    textAlign: c.alinear || (i === 0 ? 'left' : 'right'), padding: { xs: '6px 5px', md: '8px 10px' }, whiteSpace: { xs: 'normal', md: 'nowrap' },
                                    fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em',
                                    color: c.grupo === 'bot' ? COLOR.ambar : COLOR.grisTexto, borderBottom: '1px solid #e2e8f0',
                                }}
                            >
                                {c.titulo}
                            </Box>
                        ))}
                    </Box>
                </Box>
                <Box component="tbody">
                    {filas.map((fila) => (
                        <Box
                            component="tr" key={fila.clave}
                            sx={{ background: fila.destacada ? '#F8FAFC' : fila.atenuada ? '#FAFAFB' : 'transparent' }}
                        >
                            {fila.celdas.map((valor, i) => (
                                <Box
                                    component="td" key={i}
                                    sx={{
                                        textAlign: columnas[i]?.alinear || (i === 0 ? 'left' : 'right'), padding: { xs: '6px 5px', md: '7px 10px' },
                                        borderBottom: '1px solid #F1F5F9', fontSize: { xs: 12.5, md: 13 }, fontVariantNumeric: 'tabular-nums',
                                        fontWeight: fila.destacada ? 700 : 400,
                                        color: fila.atenuada ? COLOR.grisFrio : COLOR.negro,
                                        borderLeft: columnas[i]?.grupo === 'bot' && columnas[i - 1]?.grupo !== 'bot' ? '2px solid #FDF1DC' : 'none',
                                    }}
                                >
                                    {formatearValor(valor, columnas[i]?.porcentaje)}
                                </Box>
                            ))}
                        </Box>
                    ))}
                </Box>
            </Box>
        </Box>
    );
}
